import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ShieldCheck, Video, Building2, Info, HelpCircle, Lock,
  Scale, HardHat, ArrowUpRight, HeartHandshake, Sparkles,
} from "lucide-react";

export default function MediacionFamiliar() {
  const { user } = useAuth();

  const registrarMediadorCta = user ? "/app/perfil" : "/registro/mediador";
  const solicitarCta = user ? "/app" : "/registro/solicitante?next=/mediacion-familiar";

  return (
    <div className="min-h-screen" data-testid="mediacion-familiar-page">
      {/* Hero */}
      <section className="container-app pt-16 pb-10 max-w-5xl">
        <div className="label-eyebrow text-cyan-300 inline-flex items-center gap-2">
          <HeartHandshake size={12}/> Colega Link · Módulo independiente
        </div>
        <h1 className="serif text-5xl md:text-6xl mt-4 leading-[0.95] tracking-tight">
          Mediación<br className="hidden md:block"/>
          <span className="brand-gradient-text">Familiar Privada</span>
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
          Un espacio para conectar personas con mediadores familiares privados verificados, con tarifario transparente y Pago Protegido Colega Link.
        </p>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <InfoCard icon={ShieldCheck} title="Verificados" desc="Sólo mediadores con credenciales validadas y registro oficial."/>
          <InfoCard icon={Lock} title="Pago Protegido Colega Link" desc="El pago queda registrado con trazabilidad completa. Liberación administrativa por etapas."/>
          <InfoCard icon={Building2} title="Coordinación inicial" desc="La mediación ocurre fuera de Colega Link, presencial o telemática según lo informe el mediador."/>
        </div>
      </section>

      {/* Directorio en construcción */}
      <section className="container-app py-8 max-w-4xl" data-testid="directorio-en-construccion">
        <div className="panel-glow p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-[0.08]" aria-hidden="true"
            style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 8px, transparent 8px 16px)" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-[11px] font-semibold tracking-wider">
              <HardHat size={12}/> DIRECTORIO EN CONSTRUCCIÓN
            </div>
            <h2 className="serif text-3xl md:text-4xl mt-5">Estamos curando el Directorio de Mediadores</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Aún no publicamos mediadores en producción. Estamos validando registros, credenciales y disponibilidad
              real caso a caso, para garantizar que cada mediador cumpla nuestros estándares antes de aparecer aquí.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto text-left">
              <Link
                to={registrarMediadorCta}
                className="panel p-5 hover:border-cyan-400/40 transition group"
                data-testid="cta-registro-mediador"
              >
                <div className="flex items-center gap-2 text-cyan-300 text-sm font-medium">
                  <Sparkles size={14}/> ¿Eres mediador/a?
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  Postula al Directorio verificado y accede a solicitudes cuando abramos públicamente.
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs text-cyan-300 group-hover:text-cyan-200">
                  Postular como mediador <ArrowUpRight size={12}/>
                </div>
              </Link>

              <Link
                to={solicitarCta}
                className="panel p-5 hover:border-amber-400/40 transition group"
                data-testid="cta-solicitar-mediacion"
              >
                <div className="flex items-center gap-2 text-amber-300 text-sm font-medium">
                  <HeartHandshake size={14}/> ¿Necesitas mediación?
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  Regístrate como persona solicitante. Te avisaremos apenas haya mediadores disponibles.
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs text-amber-300 group-hover:text-amber-200">
                  Registrarme como solicitante <ArrowUpRight size={12}/>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Note about state free mediation */}
        <div className="mt-6 p-4 rounded-md border border-cyan-400/25 bg-cyan-400/5 text-sm text-cyan-100/90 flex items-start gap-3" data-testid="mediacion-gratuita-note">
          <Info size={16} className="text-cyan-300 mt-0.5 shrink-0"/>
          <div>
            <strong>¿Sabías esto?</strong> Si calificas para <em>mediación gratuita</em>, puedes acudir al sistema público correspondiente.
            Colega Link facilita el acceso a mediadores familiares <strong>privados</strong> verificados; no interviene en la negociación,
            no recomienda acuerdos ni garantiza resultados.
          </div>
        </div>
      </section>

      {/* Diferencia pública vs privada */}
      <section className="container-app py-10 max-w-4xl">
        <div className="label-eyebrow">Antes de decidir</div>
        <h2 className="serif text-3xl mt-2">Mediación privada vs. mediación gratuita</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="panel p-6">
            <div className="flex items-center gap-2"><Scale size={18} className="text-cyan-300"/><h3 className="serif text-xl">Mediación gratuita estatal</h3></div>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Ofrecida por el Estado a personas que califican por criterios socioeconómicos.
              Sin costo, con tiempos y disponibilidad definidos por el sistema público.
            </p>
          </div>
          <div className="panel p-6 border-amber-400/25">
            <div className="flex items-center gap-2"><HelpCircle size={18} className="text-amber-300"/><h3 className="serif text-xl">Mediación privada</h3></div>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Contratación directa a un mediador familiar privado verificado. Tú eliges el profesional,
              acuerdas el tarifario transparente y coordinas fechas. Colega Link facilita la conexión y el Pago Protegido.
            </p>
          </div>
        </div>
      </section>

      {/* Trazabilidad y modalidades */}
      <section className="container-app py-10 max-w-4xl">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="panel p-6">
            <div className="flex items-center gap-2 text-cyan-300">
              <Building2 size={16}/><span className="label-eyebrow">Modalidades soportadas</span>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li className="inline-flex items-center gap-2"><Building2 size={13}/> Presencial en oficinas del mediador.</li>
              <li className="inline-flex items-center gap-2"><Video size={13}/> Telemática (videollamada).</li>
            </ul>
          </div>
          <div className="panel p-6">
            <div className="flex items-center gap-2 text-amber-300">
              <ShieldCheck size={16}/><span className="label-eyebrow">Compromiso Colega Link</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Publicaremos el Directorio recién cuando cada mediador esté verificado. Sin listados vacíos ni promesas
              sin respaldo. Prometemos claridad de tarifas, protección del pago y trazabilidad completa del proceso.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, title, desc }) {
  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2"><Icon size={18} className="text-cyan-300"/><h3 className="font-medium">{title}</h3></div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}
